/**
 * DHD Custom Builder
 * ------------------
 * Drives step flow, BCPO/VOPO field redistribution, summary sync,
 * model cards, mobile drawer, and light validation.
 *
 * BCPO structure (discovered from live product):
 *   form > .bcpo (wrapper) > .selector-wrapper (one per field)
 *   Labels include value after colon, e.g. "Model:MF Lightning"
 *   We parse on ":" and match the left side (field name).
 */
(function () {
  'use strict';

  /* ============================================================
     CONFIG
     ============================================================ */

  /**
   * Maps BCPO field label names (lowercased, trimmed, before colon)
   * to our slot IDs in the step panels.
   */
  var FIELD_MAP = {
    'model':                'cb-vopo-model',
    'fin system':           'cb-vopo-fin-system',
    'fin configuration':    'cb-vopo-fin-configuration',
    'fin colour':           'cb-vopo-fin-colour',
    'fin color':            'cb-vopo-fin-colour',
    'length':               'cb-vopo-length',
    'width':                'cb-vopo-width',
    'thickness':            'cb-vopo-thickness',
    'volume':               'cb-vopo-volume',
    'glassing':             'cb-vopo-glassing',
    'tail shape':           'cb-vopo-tail-shape',
    'stringer colour':      'cb-vopo-stringer-colour',
    'stringer color':       'cb-vopo-stringer-colour',
    'logo colour':          'cb-vopo-logo-colour',
    'logo color':           'cb-vopo-logo-colour',
    'spray options':        'cb-vopo-spray-options',
    'spray option':         'cb-vopo-spray-options',
    'rail spray':           'cb-vopo-rail-spray',
    'add custom artwork':   'cb-vopo-upload',
    'custom art instructions': 'cb-vopo-artwork-notes',
    'notes for shaper':     'cb-vopo-notes-for-shaper',
    'notes for shaper *':   'cb-vopo-notes-for-shaper',
    'name on stringer':     'cb-vopo-name-on-stringer',
    'quantity':             'cb-vopo-quantity'
  };

  /**
   * Model → series mapping for the visual card grid.
   */
  var MODEL_SERIES = {
    pro:     ['MF Lightning', 'MF DNA', 'EE Juliette', 'SG No.8'],
    core:    ['Nexus', 'Utopia', 'Interceptor', 'Phoenix Flight', 'Phoenix', 'Black Diamond', 'Sandman'],
    travel:  ['Sweet Spot 4.0', 'EE Juliette RT'],
    twin:    ['MF TWIN', 'Twin Fin', 'Mini Twin', 'Slipstream'],
    junior:  ['MF Bolt', 'MF DNA Jnr', 'EE Juliette Jnr'],
    archive: ['3DV', '3DX', 'Dreamweaver', 'DX1 Phase 3', 'MF DNA (2024)', 'MF Eclipse', 'Double Shot', 'Monster', 'Project 15', 'Skeleton Key 2.0', 'Switchblade', 'XRS']
  };

  /**
   * Summary field → review data-attribute mapping.
   */
  var SUMMARY_FIELDS = {
    model:               { summary: 'model',       review: 'model' },
    length:              { summary: 'dimensions',   review: 'length' },
    width:               { summary: 'dimensions',   review: 'width' },
    thickness:           { summary: 'dimensions',   review: 'thickness' },
    volume:              { summary: 'dimensions',   review: 'volume' },
    'tail-shape':        { summary: 'tail-fins',    review: 'tail-shape' },
    'fin-system':        { summary: 'tail-fins',    review: 'fin-system' },
    'fin-configuration': { summary: 'tail-fins',    review: 'fin-configuration' },
    'fin-colour':        { summary: 'tail-fins',    review: 'fin-colour' },
    glassing:            { summary: 'construction',  review: 'glassing' },
    'stringer-colour':   { summary: 'construction',  review: 'stringer-colour' },
    'logo-colour':       { summary: 'construction',  review: 'logo-colour' },
    'spray-options':     { summary: 'colour',        review: 'spray-options' },
    'rail-spray':        { summary: 'colour',        review: 'rail-spray' },
    upload:              { summary: 'colour',        review: 'upload' },
    'artwork-notes':     { summary: 'colour',        review: 'artwork-notes' },
    'notes-for-shaper':  { summary: 'final',         review: 'notes-for-shaper' },
    'name-on-stringer':  { summary: 'final',         review: 'name-on-stringer' },
    quantity:            { summary: 'final',         review: 'quantity' }
  };

  /* ============================================================
     STATE
     ============================================================ */
  var currentStep = 1;
  var totalSteps = 7;
  var fieldValues = {};
  var vopoReady = false;

  /* ============================================================
     DOM HELPERS
     ============================================================ */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ============================================================
     INIT
     ============================================================ */
  function init() {
    bindNavigation();
    bindMobileSummary();
    bindSummaryNav();
    bindHeroScroll();
    bindQuantity();
    bindArchiveModal();
    waitForVOPO();
  }

  /* ============================================================
     WAIT FOR VOPO/BCPO
     ============================================================
     BCPO injects fields asynchronously after page load.
     We poll until the .bcpo wrapper has children, then act. */
  function waitForVOPO() {
    function onReady() {
      if (vopoReady) return;
      vopoReady = true;
      injectOversizePrices();
      redistributeFields();
      buildModelCards();
      syncAllFields();
      bindFieldListeners();
      updateValidation();
      initPriceSync();
    }
    function checkReady() {
      var w = document.querySelector('.bcpo, #bcpo');
      return w && w.querySelectorAll('.selector-wrapper').length > 0;
    }
    if (checkReady()) { onReady(); return; }

    // MutationObserver for fast detection
    var obs = new MutationObserver(function () {
      if (checkReady()) { obs.disconnect(); clearInterval(poll); onReady(); }
    });
    obs.observe(document.body, { childList: true, subtree: true });

    // Polling fallback — VOPO can take 20s+ on slow connections
    var poll = setInterval(function () {
      if (checkReady()) { obs.disconnect(); clearInterval(poll); onReady(); }
    }, 1000);

    setTimeout(function () {
      if (!vopoReady) {
        obs.disconnect();
        clearInterval(poll);
        console.warn('[CB] VOPO fields not found after 30s.');
      }
    }, 30000);
  }

  /* ============================================================
     REDISTRIBUTE BCPO FIELDS
     ============================================================
     Finds each .selector-wrapper inside .bcpo, reads its label,
     parses the field name (before colon), and moves the element
     into the correct step slot. Instructions blocks go to overflow. */
  function redistributeFields() {
    var bcpoWrapper = document.querySelector('.bcpo, #bcpo');
    if (!bcpoWrapper) return;

    var fieldContainers = bcpoWrapper.querySelectorAll(':scope > .selector-wrapper');
    var overflow = document.getElementById('cb-vopo-overflow');

    fieldContainers.forEach(function (container) {
      if (container.classList.contains('bcpo-instructions')) {
        if (overflow) overflow.appendChild(container);
        return;
      }

      var label = container.querySelector(':scope > .bcpo-label');
      if (!label) {
        if (overflow) overflow.appendChild(container);
        return;
      }

      var rawText = label.textContent.trim();
      var fieldName = rawText.split(':')[0].trim().toLowerCase()
        .replace(/\s*\(.*?\)\s*/g, '')
        .replace(/\s*\*.*$/, '')
        .replace(/\s+/g, ' ');

      var targetId = FIELD_MAP[fieldName];

      if (!targetId) {
        var keys = Object.keys(FIELD_MAP);
        for (var i = 0; i < keys.length; i++) {
          if (fieldName.indexOf(keys[i]) === 0 || keys[i].indexOf(fieldName) === 0) {
            targetId = FIELD_MAP[keys[i]];
            break;
          }
        }
      }

      if (targetId) {
        var slot = document.getElementById(targetId);
        if (slot) {
          // Hide the original inside .bcpo so VOPO's JS can still find it
          container.style.position = 'absolute';
          container.style.opacity = '0';
          container.style.height = '0';
          container.style.overflow = 'hidden';
          container.style.pointerEvents = 'none';
          container.setAttribute('data-cb-original', targetId);

          // Clone into our step slot (visual proxy)
          var clone = container.cloneNode(true);
          clone.style.position = '';
          clone.style.opacity = '';
          clone.style.height = '';
          clone.style.overflow = '';
          clone.style.pointerEvents = '';
          clone.removeAttribute('data-cb-original');
          clone.setAttribute('data-cb-clone', targetId);

          // Remove name attrs from clone inputs so only VOPO originals submit.
          // Radios get a prefixed name so they still group correctly.
          var cloneInputs = clone.querySelectorAll('input[name], select[name], textarea[name]');
          cloneInputs.forEach(function (inp) {
            inp.setAttribute('data-orig-name', inp.name);
            if (inp.type === 'radio') {
              inp.name = '_cb_' + inp.name;
            } else {
              inp.removeAttribute('name');
            }
          });

          slot.appendChild(clone);

          // Sync clone → original on change
          bindCloneSync(clone, container);
          return;
        }
      }

      if (overflow) overflow.appendChild(container);
    });

    // Keep VOPO's price/validation blocks visible to VOPO but hidden from user
    var extras = bcpoWrapper.querySelectorAll(':scope > .bcpo-validation, :scope > .bcpo-price-addons, :scope > .bcpo-file-check, :scope > .bcpo-addons-price');
    extras.forEach(function (el) {
      el.style.position = 'absolute';
      el.style.opacity = '0';
      el.style.height = '0';
      el.style.overflow = 'hidden';
      el.style.pointerEvents = 'none';
    });
  }

  function bindCloneSync(clone, original) {
    var cloneInputs = clone.querySelectorAll('select, input, textarea');
    var origInputs = original.querySelectorAll('select, input, textarea');

    cloneInputs.forEach(function (cloneInp, idx) {
      var origInp = origInputs[idx];
      if (!origInp) return;

      if (cloneInp.type === 'radio') {
        cloneInp.addEventListener('change', function () {
          // Check the matching original radio by value
          var origName = cloneInp.getAttribute('data-orig-name');
          if (origName) {
            var origRadios = original.querySelectorAll('input[type="radio"][name="' + origName + '"]');
            origRadios.forEach(function (r) { r.checked = (r.value === cloneInp.value); });
          }
          triggerChange(origInp);
        });
      } else if (cloneInp.type === 'checkbox') {
        cloneInp.addEventListener('change', function () {
          origInp.checked = cloneInp.checked;
          triggerChange(origInp);
        });
      } else {
        cloneInp.addEventListener('change', function () {
          origInp.value = cloneInp.value;
          triggerChange(origInp);
        });
        if (cloneInp.tagName === 'TEXTAREA' || (cloneInp.tagName === 'INPUT' && cloneInp.type === 'text')) {
          cloneInp.addEventListener('input', function () {
            origInp.value = cloneInp.value;
          });
        }
      }
    });
  }

  /* ============================================================
     MODEL CARDS
     ============================================================
     Reads options from the BCPO model select and creates
     visual card buttons grouped by series. */
  function buildModelCards() {
    var modelSlot = document.getElementById('cb-vopo-model');
    if (!modelSlot) return;

    // BCPO model field might use a <select> or radio inputs
    var select = modelSlot.querySelector('select');
    if (!select) return;

    var options = Array.prototype.slice.call(select.options);

    // Build lookup: normalised name → option value + label.
    // We key by two forms: whitespace-normalised (handles stray/double spaces
    // and non-breaking spaces) and alphanumeric-only (handles punctuation and
    // spacing differences, e.g. "Switch Blade" vs "Switchblade"). Both are
    // exact comparisons — we never substring-match, which previously collapsed
    // "MF DNA 2024" onto the plain "MF DNA" option.
    var optionLookup = {};
    var optionAlnum = {};
    options.forEach(function (opt) {
      if (!opt.value || opt.value === '' || opt.disabled) return;
      var label = opt.textContent.trim();
      var entry = { value: opt.value, label: label };
      optionLookup[normName(label)] = entry;
      optionAlnum[alnumName(label)] = entry;
    });

    // Build model image lookup from VOPO option data-image attributes
    var modelImages = {};
    options.forEach(function (opt) {
      var img = opt.getAttribute('data-image') || opt.getAttribute('data-thumbnail') || opt.getAttribute('data-img');
      if (img) modelImages[opt.textContent.trim().toLowerCase()] = img;
    });
    // Also scan swatch list items if BCPO rendered visual swatches
    var wrapper = select.closest('.selector-wrapper');
    if (wrapper) {
      var swatchImgs = wrapper.querySelectorAll('li img, .bcpo-swatch img, [data-value] img');
      swatchImgs.forEach(function (imgEl) {
        var li = imgEl.closest('[data-value], li');
        if (li) {
          var val = (li.getAttribute('data-value') || li.textContent.trim()).toLowerCase();
          if (val) modelImages[val] = imgEl.src || imgEl.getAttribute('data-src') || '';
        }
      });
    }

    var hasAnyCards = false;

    // For each series, create cards for models that exist in the dropdown
    Object.keys(MODEL_SERIES).forEach(function (seriesKey) {
      var grid = document.querySelector('[data-series-grid="' + seriesKey + '"]');
      if (!grid) return;

      var models = MODEL_SERIES[seriesKey];
      var hasCards = false;

      models.forEach(function (modelName) {
        var match = findOption(optionLookup, optionAlnum, modelName);
        if (match) {
          hasCards = true;
          hasAnyCards = true;
          var card = document.createElement('button');
          card.type = 'button';
          card.className = 'cb-model-card';
          card.textContent = match.label;
          card.setAttribute('data-model-value', match.value);
          var imgUrl = modelImages[match.label.toLowerCase()] || modelImages[match.value.toLowerCase()] || '';
          if (imgUrl) card.setAttribute('data-model-image', imgUrl);
          card.addEventListener('click', function () {
            selectModel(match.value, card);
          });
          grid.appendChild(card);
        }
      });

      // Archive renders inside a modal opened by a trigger button — its grid
      // lives outside the .cb-model-series__group, so toggle the trigger here.
      if (seriesKey === 'archive') {
        var archiveTrigger = document.getElementById('cb-archive-open');
        if (archiveTrigger) archiveTrigger.style.display = hasCards ? '' : 'none';
      }

      // Hide empty series groups
      if (!hasCards) {
        var group = grid.closest('.cb-model-series__group');
        if (group) group.style.display = 'none';
      }
    });

    // Add any remaining options not in our series mapping as "Other"
    var allMapped = [];
    Object.keys(MODEL_SERIES).forEach(function (k) {
      MODEL_SERIES[k].forEach(function (m) { allMapped.push(m.toLowerCase()); });
    });

    var unmapped = options.filter(function (opt) {
      if (!opt.value || opt.value === '' || opt.disabled) return false;
      var name = opt.textContent.trim().toLowerCase();
      // Check if this option was already matched
      return !allMapped.some(function (mapped) {
        return name === mapped || name.indexOf(mapped) !== -1 || mapped.indexOf(name) !== -1;
      });
    });

    /* "Other Models" overflow group removed – all models should be mapped to a series above */

    // Hide the raw BCPO select (keep it in DOM for form submission)
    if (hasAnyCards) {
      var wrapper = select.closest('.selector-wrapper');
      if (wrapper) {
        wrapper.style.position = 'absolute';
        wrapper.style.opacity = '0';
        wrapper.style.height = '0';
        wrapper.style.overflow = 'hidden';
        wrapper.style.pointerEvents = 'none';
      }
    }

    // If a model is already selected, highlight its card and set the image
    if (select.value) {
      var selectedCard = document.querySelector('.cb-model-card[data-model-value="' + select.value + '"]');
      if (selectedCard) {
        selectedCard.classList.add('is-selected');
        var opt = select.options[select.selectedIndex];
        if (opt) fieldValues.model = opt.textContent.trim();
        updateModelImage(selectedCard.getAttribute('data-model-image') || '', fieldValues.model);
        updateSummary();
        updateValidation();
      }
    }
  }

  function normName(s) {
    return (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }
  function alnumName(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function findOption(lookup, alnum, modelName) {
    // Exact match on whitespace-normalised name first, then on the
    // alphanumeric-only form. No substring fuzzy matching — that previously
    // let qualified names ("MF DNA 2024") collapse onto a shorter option
    // ("MF DNA"), showing the wrong label and submitting the wrong value.
    var byName = lookup[normName(modelName)];
    if (byName) return byName;
    var byAlnum = alnum[alnumName(modelName)];
    if (byAlnum) return byAlnum;
    return null;
  }

  function selectModel(value, card) {
    // Update both clone select (in our slot) and VOPO original select
    var modelSlot = document.getElementById('cb-vopo-model');
    var cloneSelect = modelSlot ? modelSlot.querySelector('select') : null;
    if (cloneSelect) {
      cloneSelect.value = value;
      triggerChange(cloneSelect);
    }
    // Also update the original VOPO select directly
    var origContainer = document.querySelector('[data-cb-original="cb-vopo-model"]');
    var origSelect = origContainer ? origContainer.querySelector('select') : null;
    if (origSelect) {
      origSelect.value = value;
      triggerChange(origSelect);
    }

    // Update card UI
    $$('.cb-model-card').forEach(function (c) {
      c.classList.remove('is-selected');
    });
    card.classList.add('is-selected');

    // Update field values and summary
    fieldValues.model = card.textContent.trim();
    updateSummary();
    updateValidation();
    updateModelImage(card.getAttribute('data-model-image') || '', card.textContent.trim());

    // If the chosen model lives in the Archive popup, close it and reflect
    // the selection on the trigger button.
    if (card && card.closest('[data-series-grid="archive"]')) {
      closeArchiveModal();
      var trigger = document.getElementById('cb-archive-open');
      if (trigger) {
        trigger.classList.add('is-selected');
        var label = trigger.querySelector('.cb-archive-trigger__current');
        if (label) label.textContent = card.textContent.trim();
      }
    }

    // Auto-scroll to the Next button so user sees the clear next action
    setTimeout(function () {
      var nextBtn = $('#cb-step-1 [data-next-step="2"]');
      if (nextBtn) nextBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  }

  /* ============================================================
     ARCHIVE MODAL
     ============================================================
     The Archive series is presented as a single trigger button that
     opens a popup of cards (older / discontinued models). Cards are
     injected into [data-series-grid="archive"] by buildModelCards. */
  function openArchiveModal() {
    var modal = document.getElementById('cb-archive-modal');
    if (!modal) return;
    // Portal to <body>: the active step has a transform, which would otherwise
    // make this position:fixed modal a child of the step rather than the viewport.
    if (modal.parentNode !== document.body) document.body.appendChild(modal);
    modal.removeAttribute('hidden');
    document.body.classList.add('cb-archive-open');
    var closeBtn = modal.querySelector('.cb-archive-modal__close');
    if (closeBtn) closeBtn.focus();
  }

  function closeArchiveModal() {
    var modal = document.getElementById('cb-archive-modal');
    if (!modal) return;
    modal.setAttribute('hidden', '');
    document.body.classList.remove('cb-archive-open');
  }

  function bindArchiveModal() {
    var trigger = document.getElementById('cb-archive-open');
    if (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        openArchiveModal();
      });
    }
    var modal = document.getElementById('cb-archive-modal');
    if (modal) {
      $$('[data-archive-close]', modal).forEach(function (el) {
        el.addEventListener('click', function (e) {
          e.preventDefault();
          closeArchiveModal();
        });
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeArchiveModal();
    });
  }

  function resolveModelImage(vopoUrl, name) {
    if (vopoUrl) return vopoUrl;
    var key = (name || '').toLowerCase().trim();
    // Shopify product catalog images (output by Liquid)
    var catalog = window.cbModelImages || {};
    if (catalog[key]) return catalog[key];
    // Fuzzy match against catalog
    var catKeys = Object.keys(catalog);
    for (var i = 0; i < catKeys.length; i++) {
      if (catKeys[i].indexOf(key) !== -1 || key.indexOf(catKeys[i]) !== -1) {
        return catalog[catKeys[i]];
      }
    }
    // Hardcoded fallback map (populate with real CDN URLs from Shopify admin)
    var fallback = window.cbModelImagesFallback || {};
    if (fallback[key]) return fallback[key];
    var fbKeys = Object.keys(fallback);
    for (var j = 0; j < fbKeys.length; j++) {
      if (fbKeys[j].indexOf(key) !== -1 || key.indexOf(fbKeys[j]) !== -1) {
        return fallback[fbKeys[j]];
      }
    }
    return '';
  }

  function updateModelImage(vopoUrl, name) {
    var url = resolveModelImage(vopoUrl, name);

    // Sticky summary sidebar
    var summaryImg = document.getElementById('cb-summary-model-img');
    var summaryImgEl = document.getElementById('cb-summary-model-img-el');
    var summaryImgName = document.getElementById('cb-summary-model-img-name');
    if (summaryImg) summaryImg.style.display = '';
    if (summaryImgName) summaryImgName.textContent = name || '';
    if (summaryImgEl) {
      if (url) { summaryImgEl.src = url; summaryImgEl.alt = name || ''; summaryImgEl.style.display = ''; }
      else { summaryImgEl.style.display = 'none'; }
    }

    // All [data-model-img] images (steps 2, 6, 7)
    $$('[data-model-img]').forEach(function (img) {
      if (url) { img.src = url; img.alt = name || ''; img.style.display = 'block'; }
      else { img.style.display = 'none'; }
    });

    // All [data-model-placeholder] fallback blocks
    $$('[data-model-placeholder]').forEach(function (el) {
      el.style.display = url ? 'none' : '';
    });

    // All [data-model-name] text spans
    $$('[data-model-name]').forEach(function (el) {
      el.textContent = name || '—';
    });
  }

  function updateStep2BuildPanel() {
    var buildName = document.getElementById('cb-step2-build-name');
    if (buildName) buildName.textContent = fieldValues.model || '—';
    // Re-apply image in case the panel was populated before model was selected
    var selectedCard = document.querySelector('.cb-model-card.is-selected');
    if (selectedCard) {
      updateModelImage(selectedCard.getAttribute('data-model-image') || '', selectedCard.textContent.trim());
    }
  }

  /* ============================================================
     STEP NAVIGATION
     ============================================================ */
  function bindNavigation() {
    // Next buttons
    $$('[data-next-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        goToStep(parseInt(btn.getAttribute('data-next-step'), 10));
      });
    });

    // Back buttons
    $$('[data-prev-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        goToStep(parseInt(btn.getAttribute('data-prev-step'), 10));
      });
    });

    // Review edit buttons
    $$('.cb-review__edit[data-go-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        goToStep(parseInt(btn.getAttribute('data-go-step'), 10));
      });
    });
  }

  function goToStep(step) {
    if (step < 1 || step > totalSteps) return;

    // Hide current
    var current = $('[data-step="' + currentStep + '"]');
    if (current) current.classList.remove('is-active');

    // Show target
    var target = $('[data-step="' + step + '"]');
    if (target) target.classList.add('is-active');

    // Mark all steps before current as complete
    var previousStep = currentStep;
    currentStep = step;

    // Update summary nav
    $$('.cb-summary__nav-item').forEach(function (item) {
      var itemStep = parseInt(item.getAttribute('data-go-step'), 10);
      item.classList.remove('is-active');
      if (itemStep === step) {
        item.classList.add('is-active');
      }
      // Mark completed: any step lower than the highest visited step
      if (itemStep < step) {
        item.classList.add('is-complete');
      }
    });

    // Update mobile header
    var mobileNum = document.getElementById('cb-mobile-step-num');
    if (mobileNum) mobileNum.textContent = step;

    // Update progress bar
    var progressBar = $('.cb-progress__bar');
    if (progressBar) {
      progressBar.style.width = ((step / totalSteps) * 100) + '%';
    }

    // Close mobile drawer
    var mobileSummary = document.getElementById('cb-mobile-summary');
    if (mobileSummary) mobileSummary.classList.remove('is-open');

    // If going to review, populate it
    if (step === 7) {
      syncAllFields();
      populateReview();
    }

    // Refresh step 2 model display when entering step 2
    if (step === 2) updateStep2BuildPanel();

    // Scroll to builder top
    var builder = document.getElementById('cb-builder');
    if (builder) {
      var offset = window.innerWidth <= 768 ? 0 : 24;
      var top = builder.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }

    updateValidation();
  }

  /* ============================================================
     MOBILE SUMMARY
     ============================================================ */
  function bindMobileSummary() {
    var toggle = document.getElementById('cb-mobile-summary-toggle');
    var mobileSummary = document.getElementById('cb-mobile-summary');
    if (!toggle || !mobileSummary) return;

    toggle.addEventListener('click', function () {
      mobileSummary.classList.toggle('is-open');
      if (mobileSummary.classList.contains('is-open')) {
        buildMobileDrawer();
      }
    });
  }

  function buildMobileDrawer() {
    var drawer = document.getElementById('cb-mobile-summary-drawer');
    if (!drawer) return;

    var steps = [
      { step: 1, label: 'Model',        value: fieldValues.model || '—' },
      { step: 2, label: 'Dimensions',    value: buildDimensionsSummary() },
      { step: 3, label: 'Tail & Fins',   value: buildTailFinsSummary() },
      { step: 4, label: 'Construction',   value: buildConstructionSummary() },
      { step: 5, label: 'Colour',         value: buildColourSummary() },
      { step: 6, label: 'Final Details',  value: buildFinalSummary() }
    ];

    var html = '';
    steps.forEach(function (s) {
      html += '<div class="cb-mobile-drawer-item" data-go-step="' + s.step + '">';
      html += '<span class="cb-mobile-drawer-item__label">' + s.label + '</span>';
      html += '<span class="cb-mobile-drawer-item__value">' + s.value + '</span>';
      html += '</div>';
    });
    drawer.innerHTML = html;

    // Bind tap-to-navigate
    $$('.cb-mobile-drawer-item', drawer).forEach(function (item) {
      item.addEventListener('click', function () {
        goToStep(parseInt(item.getAttribute('data-go-step'), 10));
      });
    });
  }

  /* ============================================================
     SUMMARY NAV — Desktop
     ============================================================ */
  function bindSummaryNav() {
    $$('.cb-summary__nav-item').forEach(function (item) {
      item.addEventListener('click', function () {
        var step = parseInt(item.getAttribute('data-go-step'), 10);
        goToStep(step);
      });
    });
  }

  /* ============================================================
     QUANTITY +/- BUTTONS
     ============================================================ */
  function bindQuantity() {
    var minus = document.getElementById('cb-qty-minus');
    var plus = document.getElementById('cb-qty-plus');
    var input = document.getElementById('cb-quantity');
    if (!minus || !plus || !input) return;

    minus.addEventListener('click', function () {
      var val = parseInt(input.value, 10) || 1;
      if (val > 1) { input.value = val - 1; triggerChange(input); }
    });
    plus.addEventListener('click', function () {
      var val = parseInt(input.value, 10) || 1;
      if (val < 10) { input.value = val + 1; triggerChange(input); }
    });
    input.addEventListener('change', function () {
      var val = parseInt(input.value, 10);
      if (isNaN(val) || val < 1) input.value = 1;
      if (val > 10) input.value = 10;
      fieldValues.quantity = input.value;
      updateSummary();
    });
  }

  /* ============================================================
     HERO SCROLL
     ============================================================ */
  function bindHeroScroll() {
    var btn = document.getElementById('cb-start-build');
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var builder = document.getElementById('cb-builder');
      if (builder) {
        var top = builder.getBoundingClientRect().top + window.pageYOffset - 24;
        window.scrollTo({ top: top, behavior: 'smooth' });
      }
    });
  }

  /* ============================================================
     FIELD LISTENERS & SYNC
     ============================================================ */
  var _syncTimer = null;
  function debouncedSync() {
    clearTimeout(_syncTimer);
    _syncTimer = setTimeout(syncAllFields, 200);
  }

  function bindFieldListeners() {
    $$('.cb-vopo-slot').forEach(function (slot) {
      var fieldKey = slot.getAttribute('data-vopo-field');
      if (!fieldKey) return;

      var inputs = slot.querySelectorAll('select, input, textarea');
      inputs.forEach(function (input) {
        input.addEventListener('change', function () {
          syncField(fieldKey, input);
        });
        if (input.tagName === 'TEXTAREA') {
          input.addEventListener('input', function () {
            syncField(fieldKey, input);
          });
        }
      });
    });

    // Single delegated listener for BCPO changes instead of per-element observers
    var form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      form.addEventListener('change', debouncedSync);
    }

    var bcpoWrapper = document.querySelector('.bcpo, #bcpo');
    if (bcpoWrapper) {
      var bcpoObs = new MutationObserver(debouncedSync);
      bcpoObs.observe(bcpoWrapper, { childList: true, characterData: true, subtree: true });
    }
  }

  function syncField(fieldKey, input) {
    var value = '';
    if (input.type === 'file') {
      value = input.files && input.files.length > 0 ? input.files[0].name : '';
    } else if (input.tagName === 'SELECT') {
      var opt = input.options[input.selectedIndex];
      value = opt ? opt.textContent.trim() : '';
    } else {
      value = input.value.trim();
    }

    fieldValues[fieldKey] = value;
    updateSummary();
    updateValidation();
  }

  function syncAllFields() {
    $$('.cb-vopo-slot').forEach(function (slot) {
      var fieldKey = slot.getAttribute('data-vopo-field');
      if (!fieldKey) return;

      // Primary source: the actual select value (always reflects user choice)
      var select = slot.querySelector('select');
      if (select) {
        var opt = select.options[select.selectedIndex];
        var selVal = opt ? opt.textContent.trim() : '';
        if (selVal && selVal !== 'Choose one' && selVal !== '') {
          fieldValues[fieldKey] = selVal;
          return;
        }
      }

      // Fallback: BCPO's .bcpo-value span (for non-select fields like radios/swatches)
      var bcpoValue = slot.querySelector('.bcpo-value');
      if (bcpoValue) {
        var val = bcpoValue.textContent.trim();
        if (val && val !== '') {
          fieldValues[fieldKey] = val;
          return;
        }
      }

      // Fallback: read from text/textarea input
      var input = slot.querySelector('input:not([type="radio"]):not([type="hidden"]):not([type="file"]), textarea');
      if (input && input.value.trim()) {
        fieldValues[fieldKey] = input.value.trim();
        return;
      }

      // Fallback: file input
      var fileInput = slot.querySelector('input[type="file"]');
      if (fileInput) {
        fieldValues[fieldKey] = (fileInput.files && fileInput.files.length > 0) ? fileInput.files[0].name : '';
        return;
      }
    });

    updateSummary();
  }

  /* ============================================================
     SUMMARY UPDATE
     ============================================================ */
  function updateSummary() {
    updatePriceDisplay();
    // Update sidebar nav values
    updateSummaryValue('model', fieldValues.model || '—');
    updateSummaryValue('dimensions', buildDimensionsSummary());
    updateSummaryValue('tail-fins', buildTailFinsSummary());
    updateSummaryValue('construction', buildConstructionSummary());
    updateSummaryValue('colour', buildColourSummary());
    updateSummaryValue('final', buildFinalSummary());
  }

  function updateSummaryValue(key, value) {
    var el = $('[data-summary="' + key + '"]');
    if (el) el.textContent = value || '—';
  }

  function buildDimensionsSummary() {
    var parts = [];
    if (fieldValues.length && fieldValues.length !== '—' && fieldValues.length !== 'Choose one') parts.push(fieldValues.length);
    if (fieldValues.width && fieldValues.width !== '—' && fieldValues.width !== 'Choose one') parts.push(fieldValues.width);
    if (fieldValues.thickness && fieldValues.thickness !== '—' && fieldValues.thickness !== 'Choose one') parts.push(fieldValues.thickness);
    return parts.length ? parts.join(' × ') : '—';
  }

  function buildTailFinsSummary() {
    var parts = [];
    if (fieldValues['tail-shape'] && fieldValues['tail-shape'] !== 'Choose one') parts.push(fieldValues['tail-shape']);
    if (fieldValues['fin-system'] && fieldValues['fin-system'] !== 'Choose one') parts.push(fieldValues['fin-system']);
    return parts.length ? parts.join(', ') : '—';
  }

  function buildConstructionSummary() {
    var g = fieldValues.glassing;
    return (g && g !== 'Choose one') ? g : '—';
  }

  function buildColourSummary() {
    var s = fieldValues['spray-options'];
    return (s && s !== 'Choose one') ? s : '—';
  }

  function buildFinalSummary() {
    var parts = [];
    if (fieldValues.quantity && fieldValues.quantity !== '1') parts.push('Qty: ' + fieldValues.quantity);
    if (fieldValues['name-on-stringer']) parts.push(fieldValues['name-on-stringer']);
    return parts.length ? parts.join(', ') : '—';
  }

  /* ============================================================
     REVIEW POPULATION
     ============================================================ */
  function populateReview() {
    Object.keys(SUMMARY_FIELDS).forEach(function (fieldKey) {
      var config = SUMMARY_FIELDS[fieldKey];
      var el = $('[data-review="' + config.review + '"]');
      if (el) {
        var value = fieldValues[fieldKey];
        if (fieldKey === 'upload') {
          el.textContent = value ? value : 'No file';
        } else {
          el.textContent = (value && value !== 'Choose one') ? value : '—';
        }
      }
    });

    // Update price on review
    var priceEl = document.getElementById('cb-review-price');
    var summaryPrice = document.getElementById('cb-summary-price');
    if (priceEl && summaryPrice) {
      priceEl.textContent = summaryPrice.textContent;
    }
  }

  /* ============================================================
     VALIDATION (light — V1)
     ============================================================ */
  function updateValidation() {
    // Step 1: require model selection
    var nextBtn1 = $('[data-next-step="2"]');
    if (nextBtn1) {
      nextBtn1.disabled = !fieldValues.model;
    }
  }

  /* ============================================================
     PRICE SYNC
     ============================================================
     Reads the Shopify variant price and BCPO's own addon total.
     We mirror BCPO's price rather than computing our own so
     the displayed price always matches what the cart will charge. */
  function initPriceSync() {
    var summaryPriceEl = document.getElementById('cb-summary-price');
    if (!summaryPriceEl) return;
    window._cbBasePriceCents = parseInt(summaryPriceEl.getAttribute('data-base-price'), 10) || 109500;
    window._cbPricePrefix = (summaryPriceEl.textContent.trim().match(/^[^0-9\-]+/) || ['$'])[0];

    var bcpoAddon = document.querySelector('.bcpo-addons-price, .bcpo-price-addons');
    if (bcpoAddon) {
      var priceObs = new MutationObserver(function () {
        updatePriceDisplay();
      });
      priceObs.observe(bcpoAddon, { childList: true, characterData: true, subtree: true });
    }

    // Listen for any form change to catch BCPO updates
    var form = document.querySelector('form[action*="/cart/add"]');
    if (form) {
      form.addEventListener('change', function () {
        setTimeout(updatePriceDisplay, 50);
      });
    }

    updatePriceDisplay();
  }

  function injectOversizePrices() {
    var bcpo = document.querySelector('.bcpo, #bcpo');
    if (!bcpo) return;
    // Find the Length select inside VOPO's original fields
    var lengthWrappers = bcpo.querySelectorAll('.selector-wrapper');
    lengthWrappers.forEach(function (w) {
      var title = w.querySelector('.bcpo-title');
      if (!title || title.textContent.trim().toLowerCase().indexOf('length') !== 0) return;
      var sel = w.querySelector('select');
      if (!sel) return;
      for (var i = 0; i < sel.options.length; i++) {
        var opt = sel.options[i];
        var inches = parseLengthInches(opt.textContent.trim());
        if (inches > OVERSIZE_THRESHOLD_INCHES) {
          opt.setAttribute('data-price', String(OVERSIZE_SURCHARGE_CENTS / 100));
        }
      }
    });
  }

  var JUNIOR_ONLY_MODELS = ['m bolt', 'mf bolt', 'ee juliette jnr', 'mf dna jnr', 'ee dna (round tail)', 'ee dna (squash tail)'];
  var OVERSIZE_THRESHOLD_INCHES = 82;
  var OVERSIZE_SURCHARGE_CENTS = 8000;

  function isJuniorOrder() {
    var model = (fieldValues.model || '').toLowerCase().trim();
    if (JUNIOR_ONLY_MODELS.indexOf(model) !== -1) return true;
    var vol = parseFloat(fieldValues.volume);
    return !isNaN(vol) && vol <= 24;
  }

  function parseLengthInches(val) {
    if (!val || val === 'As Per Model' || val === 'Choose one' || val === '—') return 0;
    var match = val.match(/(\d+)'(\d+)/);
    if (match) return parseInt(match[1], 10) * 12 + parseInt(match[2], 10);
    var ftOnly = val.match(/(\d+)'/);
    if (ftOnly) return parseInt(ftOnly[1], 10) * 12;
    return 0;
  }

  function isOversizeBoard() {
    return parseLengthInches(fieldValues.length) > OVERSIZE_THRESHOLD_INCHES;
  }

  function updateOversizeNotice() {
    var oversize = isOversizeBoard();
    var notice = document.getElementById('cb-oversize-notice');
    if (notice) notice.style.display = oversize ? '' : 'none';
    var reviewRow = document.getElementById('cb-review-oversize');
    if (reviewRow) reviewRow.style.display = oversize ? '' : 'none';
  }

  function readBcpoAddonCents() {
    var addonEl = document.querySelector('.bcpo-addons-price, .bcpo-price-addons');
    if (addonEl) {
      var text = addonEl.textContent.trim();
      if (text) {
        var num = parseFloat(text.replace(/[^0-9.]/g, '')) || 0;
        if (num > 0) return Math.round(num * 100);
      }
    }
    return 0;
  }

  function readDataPriceAddonCents() {
    var addonDollars = 0;
    // Read from VOPO originals (inside .bcpo/#bcpo) — these are the source of truth
    var bcpo = document.querySelector('.bcpo, #bcpo');
    if (!bcpo) return 0;
    bcpo.querySelectorAll('input[type="radio"]:checked[data-price]').forEach(function (r) {
      addonDollars += parseFloat(r.getAttribute('data-price')) || 0;
    });
    bcpo.querySelectorAll('select').forEach(function (sel) {
      if (sel.name === 'id' || sel.name === 'quantity') return;
      var opt = sel.options[sel.selectedIndex];
      if (opt && opt.getAttribute('data-price')) {
        addonDollars += parseFloat(opt.getAttribute('data-price')) || 0;
      }
    });
    return Math.round(addonDollars * 100);
  }

  function updatePriceDisplay() {
    var summaryPriceEl = document.getElementById('cb-summary-price');
    if (!summaryPriceEl) return;

    var baseCents = isJuniorOrder() ? 89500 : (window._cbBasePriceCents || 109500);

    // Oversize surcharge is now baked into VOPO's data-price on Length options,
    // so readDataPriceAddonCents() picks it up automatically — no manual add needed.
    var addonCents = readBcpoAddonCents() || readDataPriceAddonCents();

    var totalCents = baseCents + addonCents;
    var formatted = (window._cbPricePrefix || '$') + (totalCents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    summaryPriceEl.textContent = formatted;

    var reviewEl = document.getElementById('cb-review-price');
    if (reviewEl) reviewEl.textContent = formatted;

    var atcText = document.getElementById('cb-atc-text');
    if (atcText) atcText.textContent = 'Add to Cart — ' + formatted;

    var mobilePrice = document.getElementById('cb-mobile-price');
    if (mobilePrice) mobilePrice.textContent = formatted;

    updateOversizeNotice();
  }

  function textToCents(text) {
    var neg = text.indexOf('-') !== -1;
    var num = parseFloat(text.replace(/[^0-9.]/g, '')) || 0;
    return Math.round(num * 100) * (neg ? -1 : 1);
  }

  /* ============================================================
     UTILITY
     ============================================================ */
  function triggerChange(el) {
    if (typeof Event === 'function') {
      el.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      var evt = document.createEvent('Event');
      evt.initEvent('change', true, true);
      el.dispatchEvent(evt);
    }
  }

  /* ============================================================
     FIN UPSELL (Step 3)
     ============================================================ */
  function initFinUpsell() {
    var wrap = document.getElementById('cb-fin-upsell');
    if (!wrap) return;
    var cards = wrap.querySelectorAll('.cb-fu-card');
    if (!cards.length) return;

    function getFinSystem() {
      var slot = document.getElementById('cb-vopo-fin-system');
      if (!slot) return '';
      var sel = slot.querySelector('select');
      if (sel) {
        var val = (sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].text : '').toLowerCase();
        if (val.indexOf('fcs') !== -1) return 'fcs';
        if (val.indexOf('futures') !== -1) return 'futures';
      }
      var checked = slot.querySelector('input[type="radio"]:checked');
      if (checked) {
        var rv = (checked.value || '').toLowerCase();
        if (rv.indexOf('fcs') !== -1) return 'fcs';
        if (rv.indexOf('futures') !== -1) return 'futures';
      }
      return '';
    }

    function filterFins() {
      var sys = getFinSystem();
      var model = (fieldValues.model || '').toLowerCase().trim();
      var shown = false;

      cards.forEach(function (c) {
        if (shown) { c.style.display = 'none'; return; }
        var cSys = c.getAttribute('data-system');
        var cModelsRaw = (c.getAttribute('data-models') || '').trim();
        var modelMatch = cModelsRaw === 'all' || (!model) || cModelsRaw.split(',').map(function (s) { return s.trim(); }).indexOf(model) !== -1;
        var sysMatch = sys && cSys === sys;
        if (modelMatch && sysMatch) {
          c.style.display = '';
          shown = true;
        } else {
          c.style.display = 'none';
        }
      });

      wrap.style.display = shown ? '' : 'none';
    }

    wrap.addEventListener('click', function (e) {
      var sz = e.target.closest('.cb-fu-size');
      if (sz) {
        var card = sz.closest('.cb-fu-card');
        card.querySelectorAll('.cb-fu-size').forEach(function (s) { s.classList.remove('active'); });
        sz.classList.add('active');
        card.querySelector('.cb-fu-add').setAttribute('data-variant-id', sz.getAttribute('data-vid'));
        var priceEl = card.querySelector('.cb-fu-card__price');
        if (priceEl && sz.getAttribute('data-vprice')) priceEl.textContent = sz.getAttribute('data-vprice');
        return;
      }
      var btn = e.target.closest('.cb-fu-add');
      if (!btn) return;
      var vid = btn.getAttribute('data-variant-id');
      if (!vid) return;
      btn.textContent = '...';
      btn.disabled = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(vid, 10), quantity: 1 }] })
      }).then(function (r) { return r.json(); }).then(function () {
        btn.textContent = 'Added';
        btn.classList.add('added');
        setTimeout(function () { btn.textContent = 'Add'; btn.classList.remove('added'); btn.disabled = false; }, 2000);
      }).catch(function () {
        btn.textContent = 'Error';
        setTimeout(function () { btn.textContent = 'Add'; btn.disabled = false; }, 2000);
      });
    });

    var finSlot = document.getElementById('cb-vopo-fin-system');
    if (finSlot) {
      new MutationObserver(function () {
        var sel = finSlot.querySelector('select');
        if (sel) sel.addEventListener('change', filterFins);
        finSlot.querySelectorAll('input[type="radio"]').forEach(function (r) {
          r.addEventListener('change', filterFins);
        });
        filterFins();
      }).observe(finSlot, { childList: true, subtree: true });
    }

    var form = document.querySelector('form[action*="/cart/add"]');
    if (form) form.addEventListener('change', function () { setTimeout(filterFins, 60); });

    document.addEventListener('click', function (e) {
      if (e.target.closest('.cb-model-card')) setTimeout(filterFins, 60);
    });

    filterFins();
  }

  /* ============================================================
     UPSELL SKIP BUTTON
     ============================================================
     The Kart upsell app shows a popup after add-to-cart.
     We inject a "No, take me to checkout" button if missing. */
  function initUpsellSkip() {
    function injectSkipBtn() {
      var popup = document.querySelector('.upsellmyPopup, .action-upsell-popup');
      if (!popup || popup.querySelector('.cb-skip-upsell')) return;
      var skipBtn = document.createElement('a');
      skipBtn.href = '/checkout';
      skipBtn.className = 'cb-skip-upsell cb-btn cb-btn--outline';
      skipBtn.textContent = 'No thanks, take me to checkout';
      skipBtn.style.cssText = 'display:block;text-align:center;margin-top:16px;padding:12px 24px;';
      popup.appendChild(skipBtn);
    }
    // Inject immediately if popup DOM already exists
    injectSkipBtn();
    // Also watch for SellUp injecting it later
    var obs = new MutationObserver(injectSkipBtn);
    obs.observe(document.body, { childList: true, subtree: true });
  }

  /* ============================================================
     BOOT
     ============================================================ */
  function boot() {
    init();
    initFinUpsell();
    initUpsellSkip();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
